/* global React, Card, CardHead, Button, Badge, Input, toast */

const HOUSEHOLD_INVITE_STATUS_KEYS = { expired: 'mypage.hh.status.expired', declined: 'mypage.hh.status.declined', canceled: 'mypage.hh.status.canceled', accepted: 'mypage.hh.status.accepted' };
const HOUSEHOLD_DELIVERY_KEYS = { delivered: 'mypage.hh.delivery.delivered', opened: 'mypage.hh.delivery.opened', clicked: 'mypage.hh.delivery.clicked', failed: 'mypage.hh.delivery.failed', bounced: 'mypage.hh.delivery.bounced', complained: 'mypage.hh.delivery.complained', delivery_delayed: 'mypage.hh.delivery.delivery_delayed' };
function householdInviteStatusLabel(entry) {
  if (entry.status === 'pending') return t('mypage.hh.status.pending', { delivery: t(HOUSEHOLD_DELIVERY_KEYS[entry.emailDeliveryStatus] || 'mypage.hh.delivery.sent') });
  return HOUSEHOLD_INVITE_STATUS_KEYS[entry.status] ? t(HOUSEHOLD_INVITE_STATUS_KEYS[entry.status]) : entry.status;
}

function PersonalFinanceHouseholdCard({ personalClient }) {
  const backend = window.PlummeBackend;
  const [state, setState] = React.useState({ household: null, incomingInvites: [], outgoingInvites: [] });
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState('');
  const [error, setError] = React.useState('');
  const businessId = personalClient?.businessId;

  const refresh = React.useCallback(async () => {
    if (!backend?.getPersonalFinanceHousehold) return;
    setLoading(true);
    setError('');
    try {
      setState(await backend.getPersonalFinanceHousehold({ businessId }));
    } catch (requestError) {
      setError(requestError?.message || t('mypage.hh.loadError'));
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const run = async (key, action, success) => {
    setBusy(key);
    setError('');
    try {
      await action();
      if (success) toast(success, 'success');
      await refresh();
    } catch (requestError) {
      setError(requestError?.message || t('mypage.hh.updateError'));
    } finally {
      setBusy('');
    }
  };

  const invite = () => run('invite', async () => {
    const created = await backend.createPersonalFinanceHouseholdInvite({ businessId, email });
    setEmail('');
    toast(created?.emailDeliveryStatus === 'failed' ? t('mypage.hh.inviteEmailFailed') : t('mypage.hh.inviteSent'), created?.emailDeliveryStatus === 'failed' ? 'warning' : 'success');
  });

  const leave = () => run('leave', async () => {
    await backend.leavePersonalFinanceHousehold();
    setEmail('');
    setState((current) => ({ ...current, household: null }));
  }, t('mypage.hh.left'));

  const hideInvite = (entry) => run(`hide-${entry.id}`, () => backend.hidePersonalFinanceHouseholdInvite(entry.id), entry.status === 'pending' ? t('mypage.hh.hiddenPending') : t('mypage.hh.hidden'));

  const household = state.household;
  const partner = household?.members?.find((member) => member.businessId !== businessId)
    || household?.members?.find((member) => member.email !== backend.user?.email);

  return <Card style={{ marginTop: 28 }}>
    <CardHead icon="users" title={t('hh.household')} help={t('hh.help')} />
    {error && <p className="t-sm" style={{ color: 'var(--error)', fontWeight: 700 }}>{error}</p>}
    {loading ? <p className="t-sm muted">{t('mypage.hh.loading')}</p> : household ? <React.Fragment>
      <div className="row between gap-12" style={{ padding: '13px 15px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }}>
        <div><div className="t-h3" style={{ fontSize: 14.5 }}>{partner?.name || partner?.email || t('mypage.hh.partnerFallback')}</div><div className="t-sm muted">{t('mypage.hh.twoAccounts')}</div></div>
        <Badge variant="success" dot>{t('ap.connected')}</Badge>
      </div>
      <p className="t-sm muted" style={{ margin: '12px 0 0' }}>{t('hh.privacyNote')}</p>
      <Button variant="danger" size="sm" style={{ marginTop: 14 }} disabled={Boolean(busy)} onClick={() => {
        if (!window.confirm(t('mypage.hh.leaveConfirm'))) return;
        leave();
      }}>{busy === 'leave' ? t('mypage.hh.leaving') : t('mypage.hh.leave')}</Button>
    </React.Fragment> : <React.Fragment>
      <p className="t-sm muted" style={{ marginTop: -8 }}>{t('mypage.hh.inviteIntro')}</p>
      <div className="row gap-10" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ flex: '1 1 260px' }}><span className="t-sm" style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>{t('hh.partnerEmail')}</span><Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder={t('hh.partnerEmailPh')} /></label>
        <Button icon="plus" disabled={!email.trim() || Boolean(busy)} onClick={invite}>{busy === 'invite' ? t('mypage.hh.inviting') : t('mypage.hh.invite')}</Button>
      </div>
    </React.Fragment>}

    {!!state.incomingInvites?.length && <div style={{ marginTop: 20 }}>
      <div className="t-h3" style={{ fontSize: 14.5, marginBottom: 10 }}>{t('hh.invitesForYou')}</div>
      <div className="col gap-10">{state.incomingInvites.map((entry) => <div key={entry.id} className="row between gap-12" style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', flexWrap: 'wrap' }}>
        <div><div className="t-sm" style={{ fontWeight: 800 }}>{entry.invitedByEmail || t('mypage.hh.userFallback')}</div><div className="t-xs muted">{t('hh.wantsToLink')}</div></div>
        <div className="row gap-8"><Button variant="ghost" size="sm" disabled={Boolean(busy)} onClick={() => run(`decline-${entry.id}`, () => backend.respondPersonalFinanceHouseholdInvite({ action: 'decline', businessId, inviteId: entry.id }))} data-help={t('helpTopics.household.body')} data-help-title={t('helpTopics.household.title')}>{t('hh.decline')}</Button><Button size="sm" disabled={Boolean(busy)} onClick={() => run(`accept-${entry.id}`, () => backend.respondPersonalFinanceHouseholdInvite({ action: 'accept', businessId, inviteId: entry.id }), t('mypage.hh.joined'))} data-help={t('helpTopics.household.body')} data-help-title={t('helpTopics.household.title')}>{t('hh.accept')}</Button></div>
      </div>)}</div>
    </div>}

    {!!state.outgoingInvites?.length && !household && <div style={{ marginTop: 20 }}>
      <div className="t-h3" style={{ fontSize: 14.5, marginBottom: 10 }}>{t('mypage.hh.sentInvites')}</div>
      <div className="col gap-10">{state.outgoingInvites.map((entry) => <div key={entry.id} className="row between gap-12" style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }}>
        <div><div className="t-sm" style={{ fontWeight: 800 }}>{entry.email}</div><div className="t-xs muted">{householdInviteStatusLabel(entry)}</div></div>
        <div className="row gap-8">
          {['pending', 'expired'].includes(entry.status) && <Button variant="secondary" size="sm" disabled={Boolean(busy)} onClick={() => run(`resend-${entry.id}`, async () => { const resent = await backend.resendPersonalFinanceHouseholdInvite(entry.id); toast(resent?.emailDeliveryStatus === 'failed' ? t('mypage.hh.resendFailedToast') : t('mypage.hh.resentToast'), resent?.emailDeliveryStatus === 'failed' ? 'warning' : 'success'); })}>{entry.status === 'expired' ? t('mypage.hh.resend') : t('mypage.hh.remind')}</Button>}
          {entry.status === 'pending' && <Button variant="ghost" size="sm" disabled={Boolean(busy)} onClick={() => run(`cancel-${entry.id}`, () => backend.cancelPersonalFinanceHouseholdInvite(entry.id), t('mypage.hh.withdrawn'))} data-help={t('helpTopics.accessRole.body')} data-help-title={t('helpTopics.accessRole.title')}>{t('set.access.withdraw')}</Button>}
          <Button variant="ghost" size="sm" disabled={Boolean(busy)} onClick={() => hideInvite(entry)}>{t('mypage.hh.removeFromList')}</Button>
        </div>
      </div>)}</div>
    </div>}
  </Card>;
}

window.PersonalFinanceHouseholdCard = PersonalFinanceHouseholdCard;
